import React from 'react'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'

import Navigation from '../components/navigation'
import Footer from '../components/footer'
import { usePageView } from '../hooks/useAnalytics'
import './privacy-policy.css'

// Company information
const COMPANY_INFO = {
  legalName: "ORRAM / Sasha de Saint Tropez",
  siret: "379 892 433",
  address: {
    street: "10 place de l'Ormeau",
    postalCode: "83990",
    city: "Saint-Tropez",
    country: "FRANCE"
  },
  contact: {
    email: "orders@orram.shop",
    phone: "+33 6 17 59 64 47"
  }
}

const PrivacyPolicy = () => {
  const { t } = useTranslation('privacy')

  usePageView('Privacy Policy')

  return (
    <div className="privacy-policy-container">
      <Helmet>
        <title>{t('pageTitle')} - ORRAM Frames</title>
        <meta property="og:title" content="Privacy Policy - ORRAM Frames" />
        <meta name="description" content="ORRAM Frames Privacy Policy - How we collect, use, and protect your personal data" />
      </Helmet>
      <Navigation />

      <main className="privacy-policy-main">
        <div className="privacy-policy-content">
          <header className="privacy-policy-header">
            <h1 className="privacy-policy-title">{t('title')}</h1>
            <p className="privacy-policy-updated">{t('lastUpdated')}: January 26, 2026</p>
          </header>

          {/* Section 1: Company Information */}
          <section className="privacy-section">
            <h2>1. {t('sections.companyInfo.title')}</h2>
            <div className="company-info-box">
              <p><strong>{t('sections.companyInfo.legalName')}:</strong> {COMPANY_INFO.legalName}</p>
              <p><strong>SIRET:</strong> {COMPANY_INFO.siret}</p>
              <p><strong>{t('sections.companyInfo.address')}:</strong> {COMPANY_INFO.address.street}, {COMPANY_INFO.address.postalCode} {COMPANY_INFO.address.city}, {COMPANY_INFO.address.country}</p>
              <p><strong>{t('sections.companyInfo.email')}:</strong> <a href={`mailto:${COMPANY_INFO.contact.email}`}>{COMPANY_INFO.contact.email}</a></p>
              <p><strong>{t('sections.companyInfo.phone')}:</strong> <a href={`tel:${COMPANY_INFO.contact.phone}`}>{COMPANY_INFO.contact.phone}</a></p>
            </div>
          </section>

          {/* Section 2: Data Controller */}
          <section className="privacy-section">
            <h2>2. {t('sections.dataController.title')}</h2>
            <p>{t('sections.dataController.content')}</p>
          </section>

          {/* Section 3: Information We Collect */}
          <section className="privacy-section">
            <h2>3. {t('sections.informationCollected.title')}</h2>
            <ul className="privacy-list">
              <li><strong>{t('sections.informationCollected.personal.title')}:</strong> {t('sections.informationCollected.personal.content')}</li>
              <li><strong>{t('sections.informationCollected.shipping.title')}:</strong> {t('sections.informationCollected.shipping.content')}</li>
              <li><strong>{t('sections.informationCollected.order.title')}:</strong> {t('sections.informationCollected.order.content')}</li>
              <li><strong>{t('sections.informationCollected.payment.title')}:</strong> {t('sections.informationCollected.payment.content')}</li>
              <li><strong>{t('sections.informationCollected.technical.title')}:</strong> {t('sections.informationCollected.technical.content')}</li>
            </ul>
          </section>

          {/* Section 4: How We Use Your Information */}
          <section className="privacy-section">
            <h2>4. {t('sections.howWeUse.title')}</h2>
            <ul className="privacy-list">
              <li>{t('sections.howWeUse.purposes.orders')}</li>
              <li>{t('sections.howWeUse.purposes.confirmations')}</li>
              <li>{t('sections.howWeUse.purposes.inquiries')}</li>
              <li>{t('sections.howWeUse.purposes.improve')}</li>
              <li>{t('sections.howWeUse.purposes.legal')}</li>
            </ul>
          </section>

          {/* Section 5: Data Sharing */}
          <section className="privacy-section">
            <h2>5. {t('sections.dataSharing.title')}</h2>
            <p>{t('sections.dataSharing.intro')}</p>
            <ul className="privacy-list">
              <li><strong>Stripe</strong> - {t('sections.dataSharing.stripe')}</li>
              <li><strong>Colissimo / DB Schenker</strong> - {t('sections.dataSharing.shipping')}</li>
              <li><strong>Zoho Mail</strong> - {t('sections.dataSharing.email')}</li>
            </ul>
            <p className="privacy-highlight">{t('sections.dataSharing.noSell')}</p>
          </section>

          {/* Section 6: Cookies */}
          <section className="privacy-section">
            <h2>6. {t('sections.cookies.title')}</h2>
            <p>{t('sections.cookies.intro')}</p>
            <ul className="privacy-list">
              <li><strong>{t('sections.cookies.language.title')}</strong> - {t('sections.cookies.language.content')}</li>
              <li><strong>{t('sections.cookies.analytics.title')}</strong> - {t('sections.cookies.analytics.content')}</li>
            </ul>
            <p>{t('sections.cookies.disable')}</p>
          </section>

          {/* Section 7: Your Rights (GDPR) */}
          <section className="privacy-section">
            <h2>7. {t('sections.rights.title')}</h2>
            <p>{t('sections.rights.intro')}</p>
            <ul className="privacy-list">
              <li>{t('sections.rights.access')}</li>
              <li>{t('sections.rights.rectify')}</li>
              <li>{t('sections.rights.delete')}</li>
              <li>{t('sections.rights.object')}</li>
              <li>{t('sections.rights.portability')}</li>
              <li>{t('sections.rights.withdraw')}</li>
            </ul>
            <p>{t('sections.rights.exercise')} <a href={`mailto:${COMPANY_INFO.contact.email}`}>{COMPANY_INFO.contact.email}</a></p>
          </section>

          {/* Section 8: Data Retention */}
          <section className="privacy-section">
            <h2>8. {t('sections.retention.title')}</h2>
            <ul className="privacy-list">
              <li><strong>{t('sections.retention.orders.title')}:</strong> {t('sections.retention.orders.content')}</li>
              <li><strong>{t('sections.retention.marketing.title')}:</strong> {t('sections.retention.marketing.content')}</li>
              <li><strong>{t('sections.retention.analytics.title')}:</strong> {t('sections.retention.analytics.content')}</li>
            </ul>
          </section>

          {/* Section 9: Data Security */}
          <section className="privacy-section">
            <h2>9. {t('sections.security.title')}</h2>
            <p>{t('sections.security.content')}</p>
          </section>

          {/* Section 10: International Transfers */}
          <section className="privacy-section">
            <h2>10. {t('sections.transfers.title')}</h2>
            <p>{t('sections.transfers.content')}</p>
          </section>

          {/* Section 11: Changes to Policy */}
          <section className="privacy-section">
            <h2>11. {t('sections.changes.title')}</h2>
            <p>{t('sections.changes.content')}</p>
          </section>

          {/* Section 12: Contact */}
          <section className="privacy-section">
            <h2>12. {t('sections.contact.title')}</h2>
            <p>{t('sections.contact.content')}</p>
            <div className="contact-info-box">
              <p><strong>{t('sections.companyInfo.email')}:</strong> <a href={`mailto:${COMPANY_INFO.contact.email}`}>{COMPANY_INFO.contact.email}</a></p>
              <p><strong>{t('sections.companyInfo.phone')}:</strong> <a href={`tel:${COMPANY_INFO.contact.phone}`}>{COMPANY_INFO.contact.phone}</a></p>
              <p><strong>{t('sections.companyInfo.address')}:</strong> {COMPANY_INFO.address.street}, {COMPANY_INFO.address.postalCode} {COMPANY_INFO.address.city}, {COMPANY_INFO.address.country}</p>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default PrivacyPolicy
